/**
 * @description Seletor/Cadastro de pet para fluxo veterinário (não-defaultApp)
 * @module AppointmentVetPetSelector
 * @since Janeiro 2026
 *
 * Reaproveita UI/fluxo do agendamento interno (`VetPetList` / `VetPetFormWrapper`)
 * para permitir que o tutor selecione ou cadastre um pet dentro do modal público.
 */

import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { VetPetList } from "@/pages/schedulemanager/components/creatappointmentdialog/VetPetList";
import { VetPetFormWrapper } from "@/pages/schedulemanager/components/creatappointmentdialog/VetPetFormWrapper";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import {
  CreateVetPatientWithRelation,
  ITutorData,
} from "@/services/fhir/vetpatient/CreateVetPatientWithRelation";
import { IPetFormData } from "@/fluxvet/components/PetFormModal";

interface IPetCard {
  id: string;
  name: string;
  species?: string;
  breed?: string;
  sex?: string;
  photoUrl?: string;
}

interface AppointmentVetPetSelectorProps {
  tutorCpf: string; // CPF limpo (somente números)
  tutorData: ITutorData;
  onBack: () => void;
  onConfirm: (petId: string) => void;
}

function mapPetsFromRelatedPersonBundle(bundle: any): IPetCard[] {
  const entries: any[] = bundle?.entry || [];

  // O retorno com _include inclui RelatedPerson e Patient; queremos apenas Patient (pets).
  const patientEntries = entries.filter(
    (entry: any) => entry?.resource?.resourceType === "Patient"
  );

  return patientEntries.map((entry: any) => {
    const patient = entry.resource;

    // Busca a extensão patient-animal que contém as informações do pet
    const animalExtension = patient.extension?.find((ext: any) =>
      ext.url?.includes("patient-animal")
    );

    return {
      id: patient.id || "",
      name: patient.name?.[0]?.text || "",
      species: animalExtension?.extension?.find((ext: any) => ext.url === "species")
        ?.valueCodeableConcept?.coding?.[0]?.display,
      breed: animalExtension?.extension?.find((ext: any) => ext.url === "breed")
        ?.valueCodeableConcept?.coding?.[0]?.display,
      sex: patient.gender,
      photoUrl: patient.photo?.[0]?.data
        ? `data:${patient.photo[0].contentType};base64,${patient.photo[0].data}`
        : undefined,
    };
  });
}

export function AppointmentVetPetSelector({
  tutorCpf,
  tutorData,
  onBack,
  onConfirm,
}: AppointmentVetPetSelectorProps) {
  const [loading, setLoading] = useState(false);
  const [petsList, setPetsList] = useState<IPetCard[]>([]);
  const [selectedPetId, setSelectedPetId] = useState<string | undefined>();
  const [showPetModal, setShowPetModal] = useState(false);

  const hasPets = useMemo(() => petsList.length > 0, [petsList.length]);

  const reloadPetsList = async () => {
    setLoading(true);
    try {
      const petsResponse = await GetRelatedPerson({
        includePatient: true,
        identifier: tutorCpf,
        vetRelated: true,
        active: true,
        includePhoto: true,
      });

      setPetsList(mapPetsFromRelatedPersonBundle(petsResponse?.data));
    } catch (error) {
      console.error("Erro ao carregar pets:", error);
      toast.error("Erro ao carregar lista de pets. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadPetsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorCpf]);

  const handleSubmitPet = async (petData: IPetFormData) => {
    setLoading(true);
    try {
      const result = await CreateVetPatientWithRelation(petData, tutorData);
      if (result.success && result.petId) {
        toast.success(`Pet "${result.petName || petData.name}" cadastrado com sucesso!`);
        setShowPetModal(false);
        await reloadPetsList();
        setSelectedPetId(result.petId);
      } else {
        toast.error(result.error || "Erro ao cadastrar pet");
      }
    } catch (error) {
      console.error("Erro ao cadastrar pet:", error);
      toast.error("Erro ao cadastrar pet. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    // Se não tem pets, precisa cadastrar um antes de continuar.
    if (!hasPets) {
      toast.warn("Cadastre um pet para continuar.");
      return;
    }
    if (!selectedPetId) {
      toast.warn("Selecione um pet para continuar.");
      return;
    }
    onConfirm(selectedPetId);
  };

  return (
    <Stack spacing={2}>
      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 2,
          }}
        >
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Carregando...
          </Typography>
        </Box>
      )}

      <VetPetList
        pets={petsList}
        selectedPetId={selectedPetId}
        onSelectPet={setSelectedPetId}
        onAddNewPet={() => setShowPetModal(true)}
        onGoBack={onBack}
        onContinue={handleContinue}
      />

      <VetPetFormWrapper
        open={showPetModal}
        onClose={() => setShowPetModal(false)}
        onSubmit={handleSubmitPet}
        loading={loading}
      />
    </Stack>
  );
}


